import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast, useRole, ConfirmModal } from './shared';

export const CATEGORY = { parts: '🔩 قطع غيار', transport: '🚕 مواصلات', food: '🍽 أكل', tools: '🧰 عدة/أدوات', shipping: '📦 شحن', other: '• أخرى' };
export const money = v => Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const STATUS = {
  no_invoice: ['مفيش فاتورة لسه', 'badge-gray'],
  pending: ['مستني الدفع', 'badge-yellow'],
  earned: ['العمولة مستحقة ✓', 'badge-green'],
  loss: ['خسارة — مفيش عمولة', 'badge-red'],
};
const pct = v => `${Math.round((v || 0) * 1000) / 10}%`;
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Cairo' });

async function call(method, url, body) {
  const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'Request failed');
  return j;
}

function ExpenseForm({ f, engineers, me, isAdmin, initial, onSave, onCancel }) {
  const [x, setX] = useState(() => ({ category: 'parts', description: '', amount: '', paid_by: isAdmin ? 'company' : me, invoice_id: '', spent_at: today(), ...initial,
    ...(initial ? { paid_by: initial.paid_by || 'company', invoice_id: initial.invoice_id || '' } : {}) }));
  const [busy, setBusy] = useState(false);
  const set = k => e => setX(v => ({ ...v, [k]: e.target.value }));
  async function save() {
    setBusy(true);
    try { await onSave(x); } finally { setBusy(false); }
  }
  return (
    <div className="flex gap-8" style={{ flexWrap: 'wrap', alignItems: 'flex-end', padding: '10px 0' }}>
      <div className="field" style={{ margin: 0 }}><label>النوع</label>
        <select className="select" value={x.category} onChange={set('category')}>{Object.entries(CATEGORY).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: 150 }}><label>البيان</label>
        <input className="input" value={x.description} onChange={set('description')} placeholder="موتور / خراطيم / مواصلات ..." /></div>
      <div className="field" style={{ margin: 0, width: 110 }}><label>المبلغ</label>
        <input className="input" type="number" inputMode="decimal" min="0" value={x.amount} onChange={set('amount')} dir="ltr" autoFocus /></div>
      <div className="field" style={{ margin: 0 }}><label>دفعها مين</label>
        {isAdmin ? <select className="select" value={x.paid_by} onChange={set('paid_by')}>
          <option value="company">🏢 الشركة</option>
          {engineers.map(u => <option key={u.id} value={u.id}>👷 {u.username}</option>)}
        </select> : <input className="input" value="أنا" disabled style={{ width: 70 }} />}</div>
      {f.invoices.length > 0 && <div className="field" style={{ margin: 0 }}><label>على فاتورة</label>
        <select className="select" value={x.invoice_id} onChange={set('invoice_id')}>
          <option value="">—</option>
          {f.invoices.map(i => <option key={i.id} value={i.id}>{i.invoice_number || 'فاتورة'} ({money(i.amount)})</option>)}
        </select></div>}
      <div className="field" style={{ margin: 0 }}><label>التاريخ</label>
        <input className="input" type="date" value={x.spent_at} onChange={set('spent_at')} /></div>
      <button className="btn btn-primary btn-sm" onClick={save} disabled={busy || !(Number(x.amount) > 0)}>{busy ? '...' : 'حفظ'}</button>
      <button className="btn btn-ghost btn-sm" onClick={onCancel}>إلغاء</button>
    </div>
  );
}

/** "💰 الحسابات" card on a ticket: engineers, expenses, and the commission math */
export default function TicketFinance({ ticketId, refreshKey }) {
  const [f, setF] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const [me, setMe] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);
  const [rates, setRates] = useState(null);
  const toast = useToast();
  const role = useRole();
  const isAdmin = role === 'admin';
  const canAdd = isAdmin || role === 'technician';

  const load = useCallback(() => { call('GET', `/api/finance/tickets/${ticketId}`).then(setF).catch(e => toast(e.message, 'error')); }, [ticketId, toast]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    call('GET', '/api/finance/engineers?brief=1').then(setEngineers).catch(() => {});
    if (role === 'technician') call('GET', '/api/finance/engineers/me').then(a => setMe(a.user.id)).catch(() => {});
  }, [role]);

  if (!f) return null;
  const s = f.summary, e = f.engineers, r = f.rates;
  const [sl, sc] = STATUS[s.status];
  const run = p => p.then(setF).catch(err => toast(err.message, 'error'));
  const assign = body => run(call('PUT', `/api/finance/tickets/${ticketId}/engineers`, body));
  const mine = x => isAdmin || (role === 'technician' && x.created_by === me);

  return (
    <div className="card mb-16" dir="rtl" style={{ padding: 18 }}>
      <div className="flex items-center gap-8 mb-16" style={{ flexWrap: 'wrap' }}>
        <div className="fw-600" style={{ fontSize: 16 }}>💰 الحسابات والعمولة</div>
        <span className={`badge ${sc}`}>{sl}</span>
        {s.locked && <span className="text-xs text-dim">🔒 الحساب اتقفل (التعديل للأدمن بس)</span>}
      </div>

      {/* engineers */}
      <div className="flex gap-12 mb-16" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ margin: 0 }}><label>المهندس الأساسي</label>
          {isAdmin ? <select className="select" value={e.main_engineer_id || ''} onChange={ev => assign({ main_engineer_id: ev.target.value || null, ...(ev.target.value ? {} : { second_engineer_id: null }) })}>
            <option value="">— اختار —</option>
            {engineers.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
          </select> : <div className="fw-600">{e.main_engineer || '—'}</div>}</div>
        <div className="field" style={{ margin: 0 }}><label>مهندس مساعد (اختياري)</label>
          {isAdmin ? <select className="select" value={e.second_engineer_id || ''} disabled={!e.main_engineer_id} onChange={ev => assign({ second_engineer_id: ev.target.value || null })}>
            <option value="">— لوحده —</option>
            {engineers.filter(u => u.id !== e.main_engineer_id).map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
          </select> : <div className="fw-600">{e.second_engineer || '—'}</div>}</div>
        <div className="text-sm text-muted" style={{ paddingBottom: 8 }}>
          العمولة {pct(r.commission_rate)}{r.custom_rate && ' (خاصة)'}{e.second_engineer_id && <> · تقسيم {pct(r.main_share)} / {pct(1 - r.main_share)}{r.custom_share && ' (خاص)'}</>}
          {isAdmin && <button className="btn btn-ghost btn-sm" style={{ marginInlineStart: 6 }} onClick={() => setRates({ rate: r.custom_rate ? r.commission_rate * 100 : '', share: r.custom_share ? r.main_share * 100 : '' })}>✎ نسب التيكت</button>}
        </div>
      </div>
      {rates && <div className="flex gap-8 mb-16" style={{ flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--bg3)', padding: 10, borderRadius: 8 }}>
        <div className="field" style={{ margin: 0, width: 140 }}><label>نسبة العمولة % (فاضي = {pct(r.defaults.commission_rate)})</label>
          <input className="input" type="number" dir="ltr" value={rates.rate} onChange={ev => setRates(x => ({ ...x, rate: ev.target.value }))} /></div>
        <div className="field" style={{ margin: 0, width: 140 }}><label>نصيب الأساسي % (فاضي = {pct(r.defaults.main_share)})</label>
          <input className="input" type="number" dir="ltr" value={rates.share} onChange={ev => setRates(x => ({ ...x, share: ev.target.value }))} /></div>
        <button className="btn btn-primary btn-sm" onClick={() => { assign({ commission_rate: rates.rate === '' ? null : rates.rate / 100, main_share: rates.share === '' ? null : rates.share / 100 }); setRates(null); }}>حفظ</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setRates(null)}>إلغاء</button>
      </div>}

      {/* expenses */}
      <div className="flex items-center gap-8 mb-8">
        <div className="section-title" style={{ flex: 1, marginBottom: 0 }}>المصاريف</div>
        {canAdd && !adding && (!s.locked || isAdmin) && <button className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>+ مصروف</button>}
      </div>
      {adding && <ExpenseForm f={f} engineers={engineers} me={me} isAdmin={isAdmin} onCancel={() => setAdding(false)}
        onSave={x => call('POST', `/api/finance/tickets/${ticketId}/expenses`, x).then(d => { setF(d); setAdding(false); toast('اتسجل'); }).catch(err => toast(err.message, 'error'))} />}
      <div className="table-wrap mb-16">
        <table>
          <thead><tr><th>التاريخ</th><th>البيان</th><th>المبلغ</th><th>دفعها</th><th>فاتورة</th><th></th></tr></thead>
          <tbody>
            {f.expenses.length === 0 ? <tr><td colSpan={6}><div className="empty" style={{ padding: 18 }}>مفيش مصاريف</div></td></tr>
              : f.expenses.map(x => editing === x.id ? (
                <tr key={x.id}><td colSpan={6}><ExpenseForm f={f} engineers={engineers} me={me} isAdmin={isAdmin} initial={x} onCancel={() => setEditing(null)}
                  onSave={v => call('PUT', `/api/finance/expenses/${x.id}`, v).then(d => { setF(d); setEditing(null); }).catch(err => toast(err.message, 'error'))} /></td></tr>
              ) : (
                <tr key={x.id}>
                  <td className="mono text-sm">{x.spent_at}</td>
                  <td>{CATEGORY[x.category] || x.category}{x.description && <span className="text-muted"> — {x.description}</span>}</td>
                  <td className="mono fw-600">{money(x.amount)}</td>
                  <td className="text-sm">{x.paid_by_name ? <>👷 {x.paid_by_name}</> : '🏢 الشركة'}</td>
                  <td className="text-sm text-muted">{x.invoice_number || '—'}</td>
                  <td>{mine(x) && (!s.locked || isAdmin) && <div className="flex gap-8">
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditing(x.id)}>✎</button>
                    <button className="btn btn-danger btn-sm" onClick={() => setDel(x.id)}>✕</button>
                  </div>}</td>
                </tr>
              ))}
            {f.expenses.length > 0 && <tr><td></td><td className="fw-600">الإجمالي</td><td className="mono fw-600">{money(s.expenses)}</td><td colSpan={3}></td></tr>}
          </tbody>
        </table>
      </div>

      {/* the math */}
      <div style={{ background: 'var(--primary-dim)', borderRadius: 10, padding: 14 }}>
        <div className="flex gap-8" style={{ flexWrap: 'wrap', alignItems: 'center', fontSize: 15 }}>
          <span>الدخل <b className="mono">{money(s.income)}</b>{s.paid < s.income && <span className="text-xs text-dim"> (اتدفع {money(s.paid)})</span>}</span>
          <span className="text-dim">−</span>
          <span>المصاريف <b className="mono">{money(s.expenses)}</b></span>
          <span className="text-dim">=</span>
          <span>الصافي <b className="mono" style={{ color: s.profit < 0 ? 'var(--red)' : undefined }}>{money(s.profit)}</b></span>
          <span className="text-dim">×</span>
          <span><b className="mono">{pct(r.commission_rate)}</b></span>
          <span className="text-dim">=</span>
          <span>العمولة <b className="mono" style={{ fontSize: 18, color: 'var(--primary)' }}>{money(s.commission)}</b></span>
        </div>
        <div className="flex gap-12 mt-8" style={{ flexWrap: 'wrap' }}>
          {e.main_engineer_id ? <>
            <Link to={`/engineers/${e.main_engineer_id}`} className="badge badge-blue" style={{ fontSize: 13, padding: '5px 10px' }}>👷 {e.main_engineer}{e.second_engineer_id && ` (${pct(r.main_share)})`}: {money(s.main_amount)}</Link>
            {e.second_engineer_id && <Link to={`/engineers/${e.second_engineer_id}`} className="badge badge-blue" style={{ fontSize: 13, padding: '5px 10px' }}>👷 {e.second_engineer} ({pct(1 - r.main_share)}): {money(s.second_amount)}</Link>}
          </> : <span className="text-sm text-muted">اختار المهندس عشان العمولة تتحسب له.</span>}
        </div>
        {s.status === 'pending' && <div className="text-xs text-muted mt-8">العمولة بتدخل حساب المهندس لما كل فواتير التيكت تتعلم «مدفوعة».</div>}
      </div>
      {del && <ConfirmModal message="تمسح المصروف ده؟" onConfirm={() => { run(call('DELETE', `/api/finance/expenses/${del}`)); setDel(null); }} onClose={() => setDel(null)} />}
    </div>
  );
}
